import {ReactNode, useState} from "react";
import {Box} from "@mui/material";
import {Chip, ChipType} from "@bounce/shared/ui";
import {prettifyCurrencyRounded} from "@bounce/shared/helpers";
import BundleChip from "@bounce/shared/consumer/pages/accounts/BundleChip";
import {BundleAccountInfo} from "@bounce/shared/consumer/declarations";
import {
  BundleBalancesList,
  Card,
  Collapse,
  Container,
  Link,
  PartnerLogo,
  Text,
  ToggleButton,
} from "~/new-design/components";
import {colors, spacing} from "~/new-design/theme";
import {Pages} from "~/bi-events/enum";
import {useAmplitude} from "~/contexts";
import {useBundleAccountClick} from "~/hooks/useBundleAccountClick";
import {withStopPropagation} from "~/new-design/utils";

type AccountDetail = {
  label: string;
  value: string;
};

type AccountStatus = {label: string; variant?: ChipType["variant_"]};

type AccountCardProps = {
  creditorName: string;
  subtitle?: string;
  logo?: ReactNode;
  partnerLogoUrl?: string;
  balance: string;
  status?: AccountStatus;
  details?: AccountDetail[];
  defaultExpanded?: boolean;
  lowestMonthlyInstallment?: number;
  bundleAccounts?: BundleAccountInfo[];
  partnerCode?: string;
  page?: Pages;
  inlineToggle?: boolean;
  bundleChipInHeader?: boolean;
  onViewDetails?: () => void;
};

const TITLE_MAX_LINES = 2;

const TITLE_SX = {
  display: "-webkit-box",
  WebkitLineClamp: TITLE_MAX_LINES,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
} as const;

export const AccountCard = ({
  creditorName,
  subtitle,
  logo,
  partnerLogoUrl,
  balance,
  status,
  details = [],
  defaultExpanded = false,
  lowestMonthlyInstallment,
  bundleAccounts = [],
  partnerCode,
  page = Pages.LANDING_PAGE,
  inlineToggle = false,
  bundleChipInHeader = false,
  onViewDetails,
}: AccountCardProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isBundled = bundleAccounts.length > 0;
  const {pageClickEvent} = useAmplitude();
  const openBundleAccountDetails = useBundleAccountClick(partnerCode);
  const collapsedLabel = isBundled ? "View balances" : "View more";
  const expandedLabel = isBundled ? "Hide balances" : "View less";
  const statusChip = status?.label && (
    <Chip label={status.label} variant_={status.variant || "inherit"} size_="small" />
  );

  const handleToggle = () => {
    const viewLess = isBundled ? "hide_balances" : "view_less";
    const viewMore = isBundled ? "view_balances" : "view_more";
    const cta = expanded ? viewLess : viewMore;
    pageClickEvent({page, cta});
    setExpanded(!expanded);
  };

  const handleBalanceClick = (bundledAccountId: string) => {
    pageClickEvent({page, cta: "bundle_balance", data: {accountId: bundledAccountId}});
    openBundleAccountDetails(bundledAccountId);
  };

  return (
    <Card variant="shadow" onClick={handleToggle} sx={{gap: spacing[2], padding: spacing[4]}}>
      <Container fullWidth row gap={2.5} sx={{alignItems: "center", justifyContent: "space-between"}}>
        <Container row gap={2} sx={{alignItems: "center", minWidth: 0}}>
          {logo ?? <PartnerLogo url={partnerLogoUrl} name={creditorName} size="md" />}
          <Container gap={0} sx={{minWidth: 0}}>
            <Text variant="body-semibold" sx={TITLE_SX}>
              {creditorName}
            </Text>
            {subtitle && (
              <Text variant="caption" color={colors.grey[700]}>
                {subtitle}
              </Text>
            )}
          </Container>
        </Container>
        <Box sx={{flexShrink: 0}}>
          {isBundled && bundleChipInHeader ? <BundleChip count={bundleAccounts.length} /> : inlineToggle && statusChip}
        </Box>
      </Container>

      <Container gap={1}>
        <Container row justifyContent="space-between" alignItems="center">
          <Container row gap={2} alignItems="center">
            <Text variant="display-lg">{balance}</Text>
            {isBundled && !bundleChipInHeader && <BundleChip count={bundleAccounts.length} />}
          </Container>
          {inlineToggle ? (
            <ToggleButton
              variant="text"
              expanded={expanded}
              onClick={handleToggle}
              collapsedLabel={collapsedLabel}
              expandedLabel={expandedLabel}
            />
          ) : (
            <Box sx={{display: "flex", flexShrink: 0}}>{statusChip}</Box>
          )}
        </Container>

        {lowestMonthlyInstallment ? (
          <Container row gap={1} sx={{alignItems: "center"}}>
            <Text variant="body-medium" color={colors.grey[800]}>
              Plans from
            </Text>
            <Text variant="body-semibold" color={colors.grey[800]}>
              {prettifyCurrencyRounded(lowestMonthlyInstallment)}/mo
            </Text>
          </Container>
        ) : null}
      </Container>

      <Collapse in={expanded}>
        {isBundled ? (
          <BundleBalancesList accounts={bundleAccounts} onAccountClick={handleBalanceClick} />
        ) : (
          <Container gap={3} sx={{paddingBottom: spacing[2]}}>
            {details.length > 0 && (
              <Container gap={0}>
                {details.map((detail) => (
                  <Container row key={detail.label} gap={1}>
                    <Text key={detail.label} color={colors.grey[700]}>
                      {detail.label}:
                    </Text>
                    <Text variant="body-semibold" color={colors.grey[1000]}>
                      {detail.value}
                    </Text>
                  </Container>
                ))}
              </Container>
            )}
            {onViewDetails && (
              <Link onClick={withStopPropagation(onViewDetails)} sx={{cursor: "pointer"}}>
                View details & documents
              </Link>
            )}
          </Container>
        )}
      </Collapse>

      {!inlineToggle && (
        <Container center>
          <ToggleButton
            expanded={expanded}
            onClick={handleToggle}
            collapsedLabel={collapsedLabel}
            expandedLabel={expandedLabel}
            fullWidth
          />
        </Container>
      )}
    </Card>
  );
};
